import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["matters"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      expenseList: build.query<ExpenseListApiResponse, ExpenseListApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/`,
          params: {
            provider_id: queryArg.providerId,
            related_to_incident: queryArg.relatedToIncident,
          },
        }),
        providesTags: ["matters"],
      }),
      expenseCreate: build.mutation<
        ExpenseCreateApiResponse,
        ExpenseCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/`,
          method: "POST",
          body: queryArg.createExpense,
        }),
        invalidatesTags: ["matters"],
      }),
      expenseRetrieve: build.query<
        ExpenseRetrieveApiResponse,
        ExpenseRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/${queryArg.id}/`,
        }),
        providesTags: ["matters"],
      }),
      expenseUpdate: build.mutation<
        ExpenseUpdateApiResponse,
        ExpenseUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updateExpense,
        }),
        invalidatesTags: ["matters"],
      }),
      expenseDestroy: build.mutation<
        ExpenseDestroyApiResponse,
        ExpenseDestroyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["matters"],
      }),
      expenseReplaceAllocations: build.mutation<
        ExpenseReplaceAllocationsApiResponse,
        ExpenseReplaceAllocationsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/expenses/${queryArg.id}/allocations/`,
          method: "PUT",
          body: queryArg.createAllocationList,
        }),
        invalidatesTags: ["matters"],
      }),
      ledgerByProvider: build.query<
        LedgerByProviderApiResponse,
        LedgerByProviderApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/ledger/by-provider/`,
        }),
        providesTags: ["matters"],
      }),
      ledgerExpensesNeedingReview: build.query<
        LedgerExpensesNeedingReviewApiResponse,
        LedgerExpensesNeedingReviewApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/ledger/expenses-needing-review/`,
        }),
        providesTags: ["matters"],
      }),
      ledgerSummary: build.query<LedgerSummaryApiResponse, LedgerSummaryApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/matters/${queryArg.matterId}/ledger/summary/`,
          }),
          providesTags: ["matters"],
        },
      ),
      lienList: build.query<LienListApiResponse, LienListApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/liens/`,
          params: {
            status: queryArg.status,
          },
        }),
        providesTags: ["matters"],
      }),
      lienCreate: build.mutation<LienCreateApiResponse, LienCreateApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/liens/`,
          method: "POST",
          body: queryArg.createLien,
        }),
        invalidatesTags: ["matters"],
      }),
      lienRetrieve: build.query<LienRetrieveApiResponse, LienRetrieveApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/liens/${queryArg.id}/`,
        }),
        providesTags: ["matters"],
      }),
      lienUpdate: build.mutation<LienUpdateApiResponse, LienUpdateApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/liens/${queryArg.id}/`,
          method: "PUT",
          body: queryArg.updateLien,
        }),
        invalidatesTags: ["matters"],
      }),
      lienDestroy: build.mutation<LienDestroyApiResponse, LienDestroyApiArg>({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/liens/${queryArg.id}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["matters"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type ExpenseListApiResponse = /** status 200  */ ReadExpense[];
export type ExpenseListApiArg = {
  matterId: string;
  /** Filter by provider ID */
  providerId?: string;
  /** Filter by related_to_incident flag */
  relatedToIncident?: boolean;
};
export type ExpenseCreateApiResponse = /** status 201  */ ReadExpense;
export type ExpenseCreateApiArg = {
  matterId: string;
  createExpense: CreateExpense;
};
export type ExpenseRetrieveApiResponse = /** status 200  */ ReadExpense;
export type ExpenseRetrieveApiArg = {
  id: string;
  matterId: string;
  pk: string;
};
export type ExpenseUpdateApiResponse = /** status 200  */ ReadExpense;
export type ExpenseUpdateApiArg = {
  id: string;
  matterId: string;
  updateExpense: UpdateExpense;
};
export type ExpenseDestroyApiResponse = unknown;
export type ExpenseDestroyApiArg = {
  id: string;
  matterId: string;
};
export type ExpenseReplaceAllocationsApiResponse =
  /** status 200  */ ReadAllocationList;
export type ExpenseReplaceAllocationsApiArg = {
  id: string;
  matterId: string;
  createAllocationList: CreateAllocationList;
};
export type LedgerByProviderApiResponse = /** status 200  */ ProviderBreakdown;
export type LedgerByProviderApiArg = {
  matterId: string;
};
export type LedgerExpensesNeedingReviewApiResponse =
  /** status 200  */ ReadExpenseList;
export type LedgerExpensesNeedingReviewApiArg = {
  matterId: string;
};
export type LedgerSummaryApiResponse = /** status 200  */ LedgerSummary;
export type LedgerSummaryApiArg = {
  matterId: string;
};
export type LienListApiResponse = /** status 200  */ ReadLien[];
export type LienListApiArg = {
  matterId: string;
  /** Filter by lien status (ACTIVE or RESOLVED) */
  status?: string;
};
export type LienCreateApiResponse = /** status 201  */ ReadLien;
export type LienCreateApiArg = {
  matterId: string;
  createLien: CreateLien;
};
export type LienRetrieveApiResponse = /** status 200  */ ReadLien;
export type LienRetrieveApiArg = {
  id: string;
  matterId: string;
  pk: string;
};
export type LienUpdateApiResponse = /** status 200  */ ReadLien;
export type LienUpdateApiArg = {
  id: string;
  matterId: string;
  updateLien: UpdateLien;
};
export type LienDestroyApiResponse = unknown;
export type LienDestroyApiArg = {
  id: string;
  matterId: string;
};
export type ProviderSummary = {
  id: string | string;
  name: string;
};
export type AllocationBucket =
  | "CLIENT_SELF_PAYMENT"
  | "PIP_PAYMENT"
  | "MEDPAY_PAYMENT"
  | "HEALTH_INSURANCE_REIMBURSEMENT"
  | "PROVIDER_DEFERRED_PAYMENT"
  | "UNALLOCATED";
export type PayerType = "PROVIDER" | "COVERAGE" | "CLIENT";
export type CoverageCategory =
  | "FIRST_PARTY"
  | "FIRST_PARTY_HEALTH"
  | "THIRD_PARTY"
  | "NOT_LIABLE_INVOLVED_PARTY"
  | "UNCLEAR"
  | "PROPERTY"
  | "WORKERS_COMPENSATION";
export type CoverageSummary = {
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory;
};
export type ReadAllocation = {
  id: string | string;
  allocation_bucket: AllocationBucket;
  payer_type?: PayerType;
  payer_coverage?: CoverageSummary;
  payer_provider?: ProviderSummary;
  amount: string;
};
export type ReadExpense = {
  id: string | string;
  matter_id: string | string;
  provider_id: string | string;
  provider: ProviderSummary;
  service_date: string;
  billed_amount: string;
  description: string;
  related_to_incident: boolean;
  managed_by_system: string | null;
  external_id: string | null;
  allocations: ReadAllocation[];
  is_balanced: boolean;
  created_at: string;
  modified_at: string;
};
export type CreateAllocation = {
  allocation_bucket: AllocationBucket;
  payer_type?: PayerType;
  payer_id?: (string | string) | null;
  amount: string;
};
export type CreateExpense = {
  provider_id: string | string;
  service_date: string;
  billed_amount: string;
  description: string;
  related_to_incident?: boolean;
  managed_by_system?: string | null;
  external_id?: string | null;
  allocations?: CreateAllocation[] | null;
};
export type UpdateExpense = {
  service_date?: string | null;
  billed_amount?: string | null;
  description?: string | null;
  related_to_incident?: boolean | null;
};
export type ReadAllocationList = ReadAllocation[];
export type CreateAllocationList = CreateAllocation[];
export type ProviderAllocationBreakdown = {
  pip_paid: string;
  medpay_paid: string;
  health_insurance_paid: string;
  provider_covered: string;
  client_paid: string;
  unallocated: string;
};
export type ProviderBreakdown = {
  provider_id: string | string;
  provider: ProviderSummary;
  total_billed: string;
  by_account: ProviderAllocationBreakdown;
  has_lien: boolean;
  lien_exposure: string;
};
export type ReadExpenseList = ReadExpense[];
export type AllocationTotals = {
  client?: string;
  pip?: string;
  medpay?: string;
  health_insurance?: string;
  provider_writeoff?: string;
  unallocated?: string;
};
export type ProviderTotal = {
  provider_id: string | string;
  provider: ProviderSummary;
  total_billed: string;
};
export type BenefitType =
  | "PERSONAL_INJURY_PROTECTION"
  | "MEDICAL_PAYMENTS"
  | "UNINSURED_MOTORIST"
  | "UNDER_INSURED_MOTORIST"
  | "BODILY_INJURY_LIABILITY"
  | "PROPERTY_DAMAGE_LIABILITY"
  | "COMPREHENSIVE_AUTO"
  | "COLLISION_AUTO"
  | "WORKERS_COMPENSATION"
  | "OTHER";
export type BenefitUtilization = {
  coverage_id: string | string;
  carrier_name: string | null;
  benefit_type: BenefitType;
  limitations: {
    [key: string]: number;
  } | null;
  is_stacked: boolean | null;
  used: string;
};
export type LienholderType = "PROVIDER" | "COVERAGE";
export type LienType = "STATUTORY" | "CONTRACTUAL";
export type LienExposureDetail = {
  lien_id: string | string;
  lienholder_type: LienholderType;
  lienholder_id: string | string;
  lienholder_coverage?: CoverageSummary;
  lienholder_provider?: ProviderSummary;
  lien_type: LienType;
  amount: string;
};
export type LienExposureSummary = {
  total: string;
  by_lienholder: LienExposureDetail[];
};
export type LedgerWarning = {
  warning_type: string;
  severity: string;
  message: string;
};
export type LedgerSummary = {
  total_billed: string;
  by_bucket: AllocationTotals;
  by_provider: ProviderTotal[];
  pip: BenefitUtilization;
  medpay: BenefitUtilization;
  third_party_bi: BenefitUtilization;
  lien_exposure: LienExposureSummary;
  warnings: LedgerWarning[];
  expenses_needing_review_count: number;
};
export type LienStatus = "ACTIVE" | "RESOLVED";
export type ReadLien = {
  id: string | string;
  matter_id: string | string;
  lienholder_type: LienholderType;
  lienholder_coverage?: CoverageSummary;
  lienholder_provider?: ProviderSummary;
  lien_type: LienType;
  status: LienStatus;
  exposure_amount: string;
  created_at: string;
  modified_at: string;
};
export type CreateLien = {
  lienholder_type: LienholderType;
  lienholder_id: string | string;
  lien_type: LienType;
};
export type UpdateLien = {
  status?: LienStatus;
  lien_type?: LienType;
};
export const {
  useExpenseListQuery,
  useExpenseCreateMutation,
  useExpenseRetrieveQuery,
  useExpenseUpdateMutation,
  useExpenseDestroyMutation,
  useExpenseReplaceAllocationsMutation,
  useLedgerByProviderQuery,
  useLedgerExpensesNeedingReviewQuery,
  useLedgerSummaryQuery,
  useLienListQuery,
  useLienCreateMutation,
  useLienRetrieveQuery,
  useLienUpdateMutation,
  useLienDestroyMutation,
} = injectedRtkApi;
